/**
 * GoalEditModal — admin-only form for setting / updating the company goal.
 * Lives on the Company View page. The TV is read-only; this is the one place
 * where goals are configured. Persists via the useCompanyGoal hook
 * (localStorage shim today, real API later).
 */
import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import { PERIOD_OPTIONS } from "../../hooks/companyGoal";

const FONT = `'DM Sans', 'Inter', system-ui, sans-serif`;

const fieldStyle = {
  fontFamily: FONT,
  fontSize: "0.95rem",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid rgba(15,23,42,0.15)",
  background: "#FFFFFF",
  color: "#0B1320",
  width: "100%",
  outline: "none",
};

const labelStyle = {
  fontFamily: FONT,
  fontSize: "0.75rem",
  fontWeight: 700,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "rgba(11,19,32,0.55)",
  marginBottom: 6,
  display: "block",
};

const buttonBase = {
  fontFamily: FONT,
  fontWeight: 600,
  fontSize: "0.95rem",
  padding: "10px 18px",
  borderRadius: 10,
  border: "none",
  cursor: "pointer",
  transition: "background 150ms ease",
};

const GoalEditModal = ({ open, onClose, goal, setGoal, kpis }) => {
  const [kpiId, setKpiId] = useState(goal?.kpiId || "");
  const [target, setTarget] = useState(goal?.target ? String(goal.target) : "");
  const [period, setPeriod] = useState(goal?.period || "month");

  useEffect(() => {
    if (open) {
      setKpiId(goal?.kpiId || (kpis?.[0]?._id ?? ""));
      setTarget(goal?.target ? String(goal.target) : "");
      setPeriod(goal?.period || "month");
    }
  }, [open, goal, kpis]);

  const handleSave = () => {
    const numericTarget = Number(target);
    if (!kpiId || !Number.isFinite(numericTarget) || numericTarget <= 0) return;
    setGoal({ kpiId, target: numericTarget, period });
    onClose();
  };

  const handleClear = () => {
    setGoal(null);
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} aria-labelledby="goal-edit-title">
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "92vw", sm: 480 },
          background: "linear-gradient(180deg, #FFFFFF 0%, #F4F6FB 100%)",
          borderRadius: "8px",
          boxShadow: "0 24px 60px rgba(15,23,42,0.25)",
          p: 4,
          fontFamily: FONT,
          outline: "none",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
          <Box>
            <Box id="goal-edit-title" sx={{ fontWeight: 700, fontSize: "1.4rem", color: "#0B1320", letterSpacing: "-0.01em" }}>
              {goal ? "Edit Goal" : "Set Company Goal"}
            </Box>
            <Box sx={{ color: "rgba(11,19,32,0.55)", fontSize: "0.85rem", mt: "2px" }}>
              Sets the target shown on the TV and on this Company View
            </Box>
          </Box>
          <Box
            component="span"
            onClick={onClose}
            sx={{
              fontSize: "1.5rem",
              color: "rgba(11,19,32,0.45)",
              cursor: "pointer",
              userSelect: "none",
              lineHeight: 1,
              padding: "4px 10px",
              borderRadius: 8,
              "&:hover": { background: "rgba(15,23,42,0.06)" },
            }}
          >
            ×
          </Box>
        </Box>

        {/* KPI selector */}
        <Box sx={{ mb: 2.5 }}>
          <label style={labelStyle} htmlFor="goal-kpi">KPI to track</label>
          <select
            id="goal-kpi"
            value={kpiId}
            onChange={(e) => setKpiId(e.target.value)}
            style={fieldStyle}
          >
            <option value="" disabled>
              {kpis?.length ? "Select a KPI…" : "No KPIs available"}
            </option>
            {kpis?.map((k) => (
              <option key={k._id} value={k._id}>
                {k.title}
              </option>
            ))}
          </select>
        </Box>

        {/* Target amount */}
        <Box sx={{ mb: 2.5 }}>
          <label style={labelStyle} htmlFor="goal-target">Target amount</label>
          <input
            id="goal-target"
            type="number"
            min="1"
            step="1"
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            placeholder="e.g. 150"
            style={fieldStyle}
          />
        </Box>

        {/* Period selector */}
        <Box sx={{ mb: 3.5 }}>
          <label style={labelStyle}>Period</label>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {PERIOD_OPTIONS.map((p) => {
              const active = period === p.value;
              return (
                <button
                  type="button"
                  key={p.value}
                  onClick={() => setPeriod(p.value)}
                  style={{
                    ...buttonBase,
                    padding: "8px 14px",
                    fontSize: "0.85rem",
                    background: active ? "#3D7AC4" : "rgba(91,141,239,0.10)",
                    color: active ? "#FFFFFF" : "#3D7AC4",
                    border: active ? "1px solid #3D7AC4" : "1px solid rgba(91,141,239,0.3)",
                  }}
                >
                  {p.label}
                </button>
              );
            })}
          </Box>
        </Box>

        {/* Footer actions */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, mt: 1 }}>
          {goal ? (
            <button
              type="button"
              onClick={handleClear}
              style={{
                ...buttonBase,
                background: "transparent",
                color: "#E16C6C",
                fontSize: "0.85rem",
                padding: "8px 12px",
              }}
            >
              Clear goal
            </button>
          ) : (
            <Box />
          )}
          <Box sx={{ display: "flex", gap: 1.2 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                ...buttonBase,
                background: "rgba(15,23,42,0.06)",
                color: "rgba(11,19,32,0.7)",
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!kpiId || !Number(target) || Number(target) <= 0}
              style={{
                ...buttonBase,
                background: !kpiId || !Number(target) || Number(target) <= 0 ? "rgba(91,141,239,0.4)" : "#3D7AC4",
                color: "#FFFFFF",
                cursor: !kpiId || !Number(target) || Number(target) <= 0 ? "not-allowed" : "pointer",
                boxShadow: "0 4px 12px rgba(61,122,196,0.25)",
              }}
            >
              Save goal
            </button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default GoalEditModal;
